import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { apiFilterProducts } from '../../../api/apiMultiFilter';
import ProductGrid from './components/ProductGrid';
import FilterSelection from './components/FilterSelection';
import FilterTags from './components/FilterTags';
import ProductSearch from './components/ProductSearch';
import {
  Box,
  Typography,
  Container,
  Divider,
} from '@mui/material';
import { useMutation } from 'react-query';
import { useLocation, useNavigate, useNavigationType } from 'react-router-dom';
import posthog from '../../../utils/posthog';
import { useTypography } from '../../../context/TypographyContext';

import {
  Product,
  MultiFilterApiResponse,
  MultiFilterMutationRequest,
  MultiFilterMutation,
} from '../../../types/Multifilter.types';

const getDefaultApplications = (pathname: string): string[] => {
  const pathParts = pathname.split('/');
  const applicationIndex = pathParts.indexOf('application');
  if (applicationIndex === -1) return [];
  const applicationPath = decodeURIComponent(pathParts[applicationIndex + 1]);
  const applicationDefaults: Record<string, string[]> = {
    curtaining: ['Curtaining', 'Dual Purpose'],
    'dual purpose': ['Dual Purpose'],
    upholstery: ['Upholstery', 'Dual Purpose'],
  };
  return applicationDefaults[applicationPath.toLowerCase()] || [];
};

// Facets a custom page can lock whose selection narrows within the lock.
const NARROWABLE_LOCKED_KEYS = [
  'range', 'web_colour', 'design_style', 'web_type', 'brand',
  'web_materials', 'web_standards', 'web_composition', 'web_performance',
  'web_size', 'web_shape', 'item_attribute',
];

const mergeLocked = (
  filterObject: MultiFilterMutationRequest['filters'],
  lockedFilters: any
): MultiFilterMutationRequest['filters'] => {
  if (!lockedFilters) return filterObject;
  const merged: Record<string, any> = { ...filterObject };
  // application stays additive: the locked list isn't seeded into the user's
  // selection here (trade does that), so it must be sent alongside it.
  if (lockedFilters.application?.length) merged.application = [...(merged.application ?? []), ...lockedFilters.application];
  // A locked facet is the page's scope and the user's selection refines inside
  // it — mirrors trade's mergeLockedFilters. Picking one collection on a page
  // locked to many must send just that collection, not the whole locked list.
  // With nothing selected (or nothing inside the lock), send the full lock.
  for (const key of NARROWABLE_LOCKED_KEYS) {
    const locked: string[] | undefined = lockedFilters[key];
    if (!locked?.length) continue;
    const withinLock = ((merged[key] as string[] | undefined) ?? []).filter((v) => locked.includes(v));
    merged[key] = withinLock.length > 0 ? withinLock : locked;
  }
  // Cross-category pages (e.g. an "all Outdoor products" page scoped only by
  // item_attribute) have no item_group. Sending item_group: '' would match
  // nothing, so drop it and let the remaining base filters scope the grid.
  if (!merged.item_group) delete merged.item_group;
  return merged as MultiFilterMutationRequest['filters'];
};

const DEFAULT_FILTER_FIELDS = [
  'application', 'brand', 'range', 'web_colour',
  'design_style', 'web_materials', 'web_shape', 'web_size',
  'web_type', 'web_standards', 'web_composition', 'web_performance',
];

// ─── Scroll-position restore (return to last spot after viewing a PDP) ─────────
export const SCROLL_RESTORE_KEY = 'multifilter_restore';
const SCROLL_RESTORE_MAX_AGE_MS = 30 * 60 * 1000;
const SCROLL_RESTORE_TIMEOUT_MS = 4000;

function FilterComponent({
                           itemGroup,
                           title,
                           hideHeader,
                           lockedFilters,
                           lockedMatchTypes,
                           lockedFilterFields,
                           hoverAnimationEnabled = true,
                           onlyNewArrival = false,
                           // CMS Filtered Products block: editable heading + intro
                           // above the filter bar (H2 / P2 tokens), and `flush`
                           // drops the outer spacing so page spacers control layout.
                           sectionHeader,
                           flush = false,
                         }: any) {
  const { styleFor } = useTypography();
  const navigate = useNavigate();
  const location = useLocation();
  const navigationType = useNavigationType();

  const abortControllerRef = useRef<AbortController | null>(null);
  const restoreRef = useRef<any>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');
  // Read at request-build time so every fetch (sort, paging, filters) carries
  // the current search term as user_query — mirrors trade.
  const searchQueryRef = useRef<string>('');

  const [availableApplications, setAvailableApplications] = useState<string[]>([]);
  const [applicationsList, setApplicationsList] = useState<string[]>([]);

  // Item-group "Category" filter — shown on cross-category pages (e.g. New
  // Arrivals) where products span multiple item groups. availableCategories is
  // the API's item_group_names (code -> display name); categoryList holds the
  // chosen item-group codes. A ref mirrors the selection so makeApiCall (a
  // memoised callback) reads the latest value without going stale.
  const [availableCategories, setAvailableCategories] = useState<{ [key: string]: string }>({});
  const [categoryList, setCategoryList] = useState<string[]>([]);
  const categoryListRef = useRef<string[]>([]);

  const [availableRanges, setAvailableRanges] = useState<string[]>([]);
  // When a custom page locks Collection/Range, the sidebar offers only the
  // locked values — you refine within the page's scope, not outside it. The
  // backend returns every available facet value, so intersect here (as trade).
  const displayRanges = useMemo(() => {
    const locked = lockedFilters?.range;
    return Array.isArray(locked) && locked.length > 0
      ? availableRanges.filter((r) => locked.includes(r))
      : availableRanges;
  }, [availableRanges, lockedFilters]);
  const [availableColours, setAvailableColours] = useState<string[]>([]);
  const [availableDesigns, setAvailableDesigns] = useState<string[]>([]);
  const [availableTypes, setAvailableTypes] = useState<string[]>([]);
  const [availableBrands, setAvailableBrands] = useState<string[]>([]);
  const [availableMaterials, setAvailableMaterials] = useState<string[]>([]);
  const [availableStandards, setAvailableStandards] = useState<string[]>([]);
  const [availableCompositions, setAvailableCompositions] = useState<string[]>([]);
  const [availablePerformances, setAvailablePerformances] = useState<string[]>([]);
  const [availableSizes, setAvailableSizes] = useState<string[]>([]);
  const [availableShapes, setAvailableShapes] = useState<string[]>([]);

  const [rangeList, setRangeList] = useState<string[]>([]);
  const [colourList, setColourList] = useState<string[]>([]);
  const [designList, setDesignList] = useState<string[]>([]);
  const [typesList, setTypesList] = useState<string[]>([]);
  const [brandList, setBrandList] = useState<string[]>([]);
  const [materialList, setMaterialList] = useState<string[]>([]);
  const [standardsList, setStandardsList] = useState<string[]>([]);
  const [compositionList, setCompositionList] = useState<string[]>([]);
  const [performanceList, setPerformanceList] = useState<string[]>([]);
  const [sizeList, setSizeList] = useState<string[]>([]);
  const [shapeList, setShapeList] = useState<string[]>([]);

  const [filteredRanges, setFilteredRanges] = useState<string[]>([]);
  const [filteredColours, setFilteredColours] = useState<string[]>([]);
  const [filteredDesigns, setFilteredDesigns] = useState<string[]>([]);
  const [filteredTypes, setFilteredTypes] = useState<string[]>([]);
  const [filteredBrands, setFilteredBrands] = useState<string[]>([]);
  const [filteredMaterials, setFilteredMaterials] = useState<string[]>([]);
  const [filteredStandards, setFilteredStandards] = useState<string[]>([]);
  const [filteredCompositions, setFilteredCompositions] = useState<string[]>([]);
  const [filteredPerformances, setFilteredPerformances] = useState<string[]>([]);
  const [filteredSizes, setFilteredSizes] = useState<string[]>([]);
  const [filteredShapes, setFilteredShapes] = useState<string[]>([]);

  const [productItemCount, setProductItemCount] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(0);

  const [sortBySetting, setSortBySetting] = useState<string>('Alphabetical A-Z');
  const [itemsPerPage, setItemsPerPage] = useState<number>(27);
  const [showAll, setShowAll] = useState<boolean>(true);
  const [accumulatedProducts, setAccumulatedProducts] = useState<Product[]>([]);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  // Set when a load-more request fails so the grid stops auto-retrying (its
  // scroll sentinel would otherwise re-fire the instant loading ends) and shows
  // a manual retry instead. Cleared by the next filter/sort/page request.
  const [loadMoreFailed, setLoadMoreFailed] = useState<boolean>(false);
  const loadingMoreRef = useRef<boolean>(false);

  const anchorRef = useRef<HTMLDivElement>(null);
  const debouncedApiCall = useRef<NodeJS.Timeout | null>(null);

  // Sticky filter column: pin just below the sticky navbar and let the column
  // scroll independently (capped at the viewport height) while the product grid
  // scrolls with the page — mirrors trade.
  const filterScrollRef = useRef<HTMLDivElement | null>(null);
  const [navOffset, setNavOffset] = useState<number>(0);

  const buildMatchTypes = useCallback(() => {
    if (!lockedMatchTypes || Object.keys(lockedMatchTypes).length === 0) return undefined;
    return lockedMatchTypes;
  }, [lockedMatchTypes]);

  // Measure the sticky navbar height so the filter column pins just below it.
  useEffect(() => {
    const appBar = document.querySelector('.MuiAppBar-root') as HTMLElement | null;
    const measure = () => setNavOffset(appBar ? appBar.offsetHeight : 0);
    measure();
    const ro = appBar ? new ResizeObserver(measure) : null;
    if (ro && appBar) ro.observe(appBar);
    window.addEventListener('resize', measure);
    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, []);

  // Auto-hide the filter column scrollbar: show the thumb only while scrolling.
  useEffect(() => {
    const el = filterScrollRef.current;
    if (!el) return;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const onScroll = () => {
      el.classList.add('is-scrolling');
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => el.classList.remove('is-scrolling'), 700);
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      el.removeEventListener('scroll', onScroll);
      if (timer) clearTimeout(timer);
    };
  }, []);

  const makeApiCall = useCallback(async (requestData: MultiFilterMutationRequest): Promise<MultiFilterApiResponse> => {
    try {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();
      // Single funnel for every product request. Two page-level custom-page
      // settings (from the CMS category, passed via FilteredProductsBlockRenderer)
      // are applied here so all call sites — initial load, sort, paging, filter
      // changes — carry them consistently, mirroring trade:
      //   • lockedFilterFields → which facets the CMS page exposes.
      //   • onlyNewArrival → scope the grid to new-arrival products.
      //   • item-group Category selection → item groups chosen in the cross-
      //     category Category filter scope the grid (read from a ref so paging/
      //     sort requests carry the current selection).
      const selectedCats = categoryListRef.current;
      const enrichedRequest: MultiFilterMutationRequest = {
        ...requestData,
        ...(selectedCats.length > 0
          ? { filters: { ...requestData.filters, item_group: selectedCats as any } }
          : {}),
        ...(Array.isArray(lockedFilterFields) && lockedFilterFields.length > 0
          ? { filter_fields: lockedFilterFields }
          : {}),
        ...(onlyNewArrival ? { only_new_arrival: true } : {}),
      };
      const response = await apiFilterProducts(enrichedRequest);
      return response;
    } catch (error: any) {
      if (error.name === 'AbortError') throw error;
      console.error('API call failed:', error);
      throw error;
    }
  }, [lockedFilterFields, onlyNewArrival]);

  const getProductsMutation: MultiFilterMutation = useMutation<MultiFilterApiResponse, Error, MultiFilterMutationRequest>(
    makeApiCall,
    {
      onMutate: () => {
        setLoadMoreFailed(false);
      },
      onSuccess: (response) => {
        const filters = response.filters;
        setAvailableApplications(filters?.application || []);
        setAvailableRanges(filters?.range || []);
        setAvailableColours(filters?.web_colour || []);
        setAvailableDesigns(filters?.design_style || []);
        setAvailableTypes(filters?.web_type || []);
        setAvailableBrands(filters?.brand || []);
        setAvailableMaterials(filters?.web_materials?.filter((m: string) => m !== '-') || []);
        setAvailableStandards(filters?.web_standards?.filter((s: string) => s !== '-') || []);
        setAvailableCompositions(filters?.web_composition?.filter((c: string) => c !== '-') || []);
        setAvailablePerformances(filters?.web_performance?.filter((p: string) => p !== '-') || []);
        setAvailableSizes(filters?.web_size || []);
        setAvailableShapes(filters?.web_shape || []);
        // item_group_names is returned only when 'item_group' is in filter_fields
        // (cross-category pages) — it powers the item-group Category filter.
        // Consumer only sells Fabric (005) / Wallpaper (001), so restrict the
        // Category options to those regardless of what the backend returns.
        const rawItemGroups = (filters as any)?.item_group_names || {};
        const consumerItemGroups = Object.fromEntries(
          Object.entries(rawItemGroups).filter(([code]) => code === '001' || code === '005')
        );
        setAvailableCategories(consumerItemGroups as { [key: string]: string });
        setFilteredRanges(filters?.range || []);
        setFilteredColours(filters?.web_colour || []);
        setFilteredDesigns(filters?.design_style || []);
        setFilteredTypes(filters?.web_type || []);
        setFilteredBrands(filters?.brand || []);
        setFilteredMaterials(filters?.web_materials?.filter((m: string) => m !== '-') || []);
        setFilteredStandards(filters?.web_standards?.filter((s: string) => s !== '-') || []);
        setFilteredCompositions(filters?.web_composition?.filter((c: string) => c !== '-') || []);
        setFilteredPerformances(filters?.web_performance?.filter((p: string) => p !== '-') || []);
        setFilteredSizes(filters?.web_size || []);
        setFilteredShapes(filters?.web_shape || []);
        setProductItemCount(response.total_items_count);

        if (restoreRef.current?.showAll) {
          setAccumulatedProducts(response.products || []);
          setTotalPages(Math.ceil(response.total_items_count / 50));
          restoreRef.current = null;
        } else {
          const isAll = itemsPerPage === -1;
          setTotalPages(isAll ? 1 : Math.ceil(response.total_items_count / (itemsPerPage || 27)));
          if (!isAll) setAccumulatedProducts([]);
        }
      },
      onError: (error: Error) => {
        if (error.name !== 'AbortError') console.error('Error fetching products:', error);
      },
    }
  );

  const applyFiltersImmediately = useCallback(
    (
      updatedColours?: string[],
      updatedDesigns?: string[],
      updatedTypes?: string[],
      updatedBrands?: string[],
      updatedRanges?: string[],
      updatedMaterials?: string[],
      updatedStandards?: string[],
      updatedCompositions?: string[],
      updatedPerformances?: string[],
      updatedSizes?: string[],
      updatedShapes?: string[],
      updatedApplications?: string[]
    ) => {
      if (debouncedApiCall.current) clearTimeout(debouncedApiCall.current);
      clearScrollRestore();

      const applicationsToUse = updatedApplications || applicationsList;
      const rangesToUse = updatedRanges !== undefined ? updatedRanges : rangeList;
      const coloursToUse = updatedColours !== undefined ? updatedColours : colourList;
      const designsToUse = updatedDesigns !== undefined ? updatedDesigns : designList;
      const typesToUse = updatedTypes !== undefined ? updatedTypes : typesList;
      const brandsToUse = updatedBrands !== undefined ? updatedBrands : brandList;
      const materialsToUse = updatedMaterials !== undefined ? updatedMaterials : materialList;
      const standardsToUse = updatedStandards !== undefined ? updatedStandards : standardsList;
      const compositionsToUse = updatedCompositions !== undefined ? updatedCompositions : compositionList;
      const performancesToUse = updatedPerformances !== undefined ? updatedPerformances : performanceList;
      const sizesToUse = updatedSizes !== undefined ? updatedSizes : sizeList;
      const shapesToUse = updatedShapes !== undefined ? updatedShapes : shapeList;

      const currentParams = new URLSearchParams();
      applicationsToUse.forEach(app => currentParams.append('application', app));
      rangesToUse.forEach(r => currentParams.append('range', r));
      coloursToUse.forEach(c => currentParams.append('colours', c));
      designsToUse.forEach(d => currentParams.append('design_style', d));
      typesToUse.forEach(t => currentParams.append('types', t));
      brandsToUse.forEach(b => currentParams.append('brand', b));
      materialsToUse.forEach(m => currentParams.append('material', m));
      standardsToUse.forEach(s => currentParams.append('standards', s));
      compositionsToUse.forEach(c => currentParams.append('composition', c));
      performancesToUse.forEach(p => currentParams.append('performance', p));
      sizesToUse.forEach(s => currentParams.append('sizes', s));
      shapesToUse.forEach(s => currentParams.append('shapes', s));
      if (searchQueryRef.current) currentParams.set('search', searchQueryRef.current);
      currentParams.set('page', '1');
      currentParams.set('startoffset', '0');
      currentParams.set('endoffset', itemsPerPage.toString());
      currentParams.set('sort', sortBySetting);
      currentParams.set('itemsPerPage', itemsPerPage.toString());

      if (typeof window !== 'undefined') {
        window.history.replaceState({ path: `${location.pathname}?${currentParams.toString()}` }, '', `${location.pathname}?${currentParams.toString()}`);
      }

      debouncedApiCall.current = setTimeout(() => {
        let filterObject: MultiFilterMutationRequest['filters'] = { item_group: itemGroup };
        if (applicationsToUse.length > 0) filterObject.application = applicationsToUse;
        if (rangesToUse.length > 0) filterObject.range = rangesToUse;
        if (coloursToUse.length > 0) filterObject.web_colour = coloursToUse;
        if (designsToUse.length > 0) filterObject.design_style = designsToUse;
        if (brandsToUse.length > 0) filterObject.brand = brandsToUse;
        if (materialsToUse.length > 0) filterObject.web_materials = materialsToUse;
        if (standardsToUse.length > 0) filterObject.web_standards = standardsToUse;
        if (compositionsToUse.length > 0) filterObject.web_composition = compositionsToUse;
        if (performancesToUse.length > 0) filterObject.web_performance = performancesToUse;
        if (sizesToUse.length > 0) filterObject.web_size = sizesToUse;
        if (shapesToUse.length > 0) filterObject.web_shape = shapesToUse;
        if (typesToUse.length > 0) filterObject.web_type = typesToUse;
        filterObject = mergeLocked(filterObject, lockedFilters);

        const isAll = itemsPerPage === -1;

        getProductsMutation.mutate({
          filters: filterObject,
          filter_fields: DEFAULT_FILTER_FIELDS,
          filter_match_types: buildMatchTypes(),
          main_filter_field: null,
          selection_fields: null,
          sort_by_setting: sortBySetting,
          off_setter: { start: 0, end: isAll ? 50 : itemsPerPage },
          only_published: true,
          only_promo: false,
          only_in_stock: false,
          user_query: searchQueryRef.current || undefined,
          limited: true,
        });
      }, 300);

      setCurrentPage(1);
    },
    [
      applicationsList, rangeList, colourList, designList, typesList, brandList, materialList,
      standardsList, compositionList, performanceList, sizeList, shapeList, itemGroup, location.pathname,
      itemsPerPage, getProductsMutation, sortBySetting, lockedFilters, buildMatchTypes,
    ]
  );

  const handleCheckboxChange = (
    elem: string,
    setList: React.Dispatch<React.SetStateAction<string[]>>,
    list: string[],
    paramKey: string
  ) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked;
    const updatedList = isChecked ? [...list, elem] : list.filter(item => item !== elem);
    setList(updatedList);

    if (isChecked) {
      posthog.capture('filter_applied', {
        filter_type: paramKey,
        filter_value: elem,
        source: 'consumer',
      });
    }

    switch (paramKey) {
      case 'colours': applyFiltersImmediately(updatedList); break;
      case 'design_style': applyFiltersImmediately(undefined, updatedList); break;
      case 'types': applyFiltersImmediately(undefined, undefined, updatedList); break;
      case 'brand': applyFiltersImmediately(undefined, undefined, undefined, updatedList); break;
      case 'range': applyFiltersImmediately(undefined, undefined, undefined, undefined, updatedList); break;
      case 'material': applyFiltersImmediately(undefined, undefined, undefined, undefined, undefined, updatedList); break;
      case 'standards': applyFiltersImmediately(undefined, undefined, undefined, undefined, undefined, undefined, updatedList); break;
      case 'composition': applyFiltersImmediately(undefined, undefined, undefined, undefined, undefined, undefined, undefined, updatedList); break;
      case 'performance': applyFiltersImmediately(undefined, undefined, undefined, undefined, undefined, undefined, undefined, undefined, updatedList); break;
      case 'sizes': applyFiltersImmediately(undefined, undefined, undefined, undefined, undefined, undefined, undefined, undefined, undefined, updatedList); break;
      case 'shapes': applyFiltersImmediately(undefined, undefined, undefined, undefined, undefined, undefined, undefined, undefined, undefined, undefined, updatedList); break;
    }
  };

  const handleRemoveFilter = (category: string, value: string) => {
    switch (category) {
      case 'colours': { const n = colourList.filter(i => i !== value); setColourList(n); applyFiltersImmediately(n); break; }
      case 'design_style': { const n = designList.filter(i => i !== value); setDesignList(n); applyFiltersImmediately(undefined, n); break; }
      case 'types': { const n = typesList.filter(i => i !== value); setTypesList(n); applyFiltersImmediately(undefined, undefined, n); break; }
      case 'brand': { const n = brandList.filter(i => i !== value); setBrandList(n); applyFiltersImmediately(undefined, undefined, undefined, n); break; }
      case 'range': { const n = rangeList.filter(i => i !== value); setRangeList(n); applyFiltersImmediately(undefined, undefined, undefined, undefined, n); break; }
      case 'material': { const n = materialList.filter(i => i !== value); setMaterialList(n); applyFiltersImmediately(undefined, undefined, undefined, undefined, undefined, n); break; }
      case 'standards': { const n = standardsList.filter(i => i !== value); setStandardsList(n); applyFiltersImmediately(undefined, undefined, undefined, undefined, undefined, undefined, n); break; }
      case 'composition': { const n = compositionList.filter(i => i !== value); setCompositionList(n); applyFiltersImmediately(undefined, undefined, undefined, undefined, undefined, undefined, undefined, n); break; }
      case 'performance': { const n = performanceList.filter(i => i !== value); setPerformanceList(n); applyFiltersImmediately(undefined, undefined, undefined, undefined, undefined, undefined, undefined, undefined, n); break; }
      case 'sizes': { const n = sizeList.filter(i => i !== value); setSizeList(n); applyFiltersImmediately(undefined, undefined, undefined, undefined, undefined, undefined, undefined, undefined, undefined, n); break; }
      case 'shapes': { const n = shapeList.filter(i => i !== value); setShapeList(n); applyFiltersImmediately(undefined, undefined, undefined, undefined, undefined, undefined, undefined, undefined, undefined, undefined, n); break; }
    }
  };

  const handleClearAllFilters = () => {
    setRangeList([]);
    setColourList([]);
    setDesignList([]);
    setTypesList([]);
    setBrandList([]);
    setMaterialList([]);
    setStandardsList([]);
    setCompositionList([]);
    setPerformanceList([]);
    setSizeList([]);
    setShapeList([]);
    setCategoryList([]);
    categoryListRef.current = [];
    applyFiltersImmediately([], [], [], [], [], [], [], [], [], [], []);
  };

  const handleSearchChange = (query: string) => {
    clearScrollRestore();
    searchQueryRef.current = query;
    setSearchQuery(query);
    // Rebuild the URL (carrying the search term) and refetch with user_query.
    applyFiltersImmediately();
  };

  const applyFiltersForApplications = (updatedApplications: string[]) => {
    saveCurrentPosition();
    applyFiltersImmediately(undefined, undefined, undefined, undefined, undefined, undefined, undefined, undefined, undefined, updatedApplications);
  };

  // Item-group Category selection. makeApiCall reads categoryListRef, so update
  // the ref first, then refetch — the new selection scopes the request's item_group.
  const handleCategoryChange = (updatedCategories: string[]) => {
    saveCurrentPosition();
    categoryListRef.current = updatedCategories;
    setCategoryList(updatedCategories);
    applyFiltersImmediately();
  };

  const handleLoadMore = async () => {
    // The ref (not the state) is the guard: two observer callbacks can land
    // before the loadingMore state has re-rendered.
    if (loadingMoreRef.current) return;
    loadingMoreRef.current = true;
    setLoadMoreFailed(false);
    setLoadingMore(true);
    try {
      const currentProductCount = accumulatedProducts.length > 0
        ? accumulatedProducts.length
        : (getProductsMutation.data?.products?.length || 0);

      let filterObject: MultiFilterMutationRequest['filters'] = { item_group: itemGroup };
      if (applicationsList.length > 0) filterObject.application = applicationsList;
      if (rangeList.length > 0) filterObject.range = rangeList;
      if (colourList.length > 0) filterObject.web_colour = colourList;
      if (designList.length > 0) filterObject.design_style = designList;
      if (typesList.length > 0) filterObject.web_type = typesList;
      if (brandList.length > 0) filterObject.brand = brandList;
      if (materialList.length > 0) filterObject.web_materials = materialList;
      if (standardsList.length > 0) filterObject.web_standards = standardsList;
      if (compositionList.length > 0) filterObject.web_composition = compositionList;
      if (performanceList.length > 0) filterObject.web_performance = performanceList;
      if (sizeList.length > 0) filterObject.web_size = sizeList;
      if (shapeList.length > 0) filterObject.web_shape = shapeList;
      filterObject = mergeLocked(filterObject, lockedFilters);

      const response = await makeApiCall({
        filters: filterObject,
        filter_fields: DEFAULT_FILTER_FIELDS,
        filter_match_types: buildMatchTypes(),
        main_filter_field: null,
        selection_fields: null,
        sort_by_setting: sortBySetting,
        off_setter: { start: currentProductCount, end: currentProductCount + 50 },
        only_published: true,
        only_promo: false,
        only_in_stock: false,
        user_query: searchQueryRef.current || undefined,
        limited: true,
      });

      setAccumulatedProducts(prev => {
        const existing = prev.length > 0 ? prev : (getProductsMutation.data?.products || []);
        return [...existing, ...response.products];
      });
    } catch (error) {
      console.error('Error loading more products:', error);
      setLoadMoreFailed(true);
    } finally {
      loadingMoreRef.current = false;
      setLoadingMore(false);
    }
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    clearScrollRestore();
    const isAll = newItemsPerPage === -1;
    setItemsPerPage(newItemsPerPage);
    setShowAll(isAll);
    setCurrentPage(1);
    setAccumulatedProducts([]);

    const currentParams = new URLSearchParams(window.location.search);
    currentParams.set('itemsPerPage', newItemsPerPage.toString());
    currentParams.set('page', '1');
    currentParams.set('startoffset', '0');
    currentParams.set('endoffset', isAll ? '50' : newItemsPerPage.toString());
    currentParams.set('sort', sortBySetting);
    window.history.replaceState({ path: `${location.pathname}?${currentParams.toString()}` }, '', `${location.pathname}?${currentParams.toString()}`);

    let filterObject: MultiFilterMutationRequest['filters'] = { item_group: itemGroup };
    if (applicationsList.length > 0) filterObject.application = applicationsList;
    if (rangeList.length > 0) filterObject.range = rangeList;
    if (colourList.length > 0) filterObject.web_colour = colourList;
    if (designList.length > 0) filterObject.design_style = designList;
    if (typesList.length > 0) filterObject.web_type = typesList;
    if (brandList.length > 0) filterObject.brand = brandList;
    if (materialList.length > 0) filterObject.web_materials = materialList;
    if (standardsList.length > 0) filterObject.web_standards = standardsList;
    if (sizeList.length > 0) filterObject.web_size = sizeList;
    if (shapeList.length > 0) filterObject.web_shape = shapeList;
    filterObject = mergeLocked(filterObject, lockedFilters);

    getProductsMutation.mutate({
      filters: filterObject,
      filter_fields: DEFAULT_FILTER_FIELDS,
      filter_match_types: buildMatchTypes(),
      main_filter_field: null,
      selection_fields: null,
      sort_by_setting: sortBySetting,
      off_setter: { start: 0, end: isAll ? 50 : newItemsPerPage },
      only_published: true,
      only_promo: false,
      only_in_stock: false,
      user_query: searchQueryRef.current || undefined,
      limited: true,
    });
  };

  const handleSortChange = (newSortValue: string) => {
    clearScrollRestore();
    setSortBySetting(newSortValue);
    setCurrentPage(1);

    const isAll = itemsPerPage === -1;
    const currentParams = new URLSearchParams(window.location.search);
    currentParams.set('sort', newSortValue);
    currentParams.set('page', '1');
    currentParams.set('startoffset', '0');
    currentParams.set('endoffset', isAll ? '50' : itemsPerPage.toString());
    currentParams.set('itemsPerPage', itemsPerPage.toString());
    window.history.replaceState({ path: `${location.pathname}?${currentParams.toString()}` }, '', `${location.pathname}?${currentParams.toString()}`);

    let filterObject: MultiFilterMutationRequest['filters'] = { item_group: itemGroup };
    if (applicationsList.length > 0) filterObject.application = applicationsList;
    if (rangeList.length > 0) filterObject.range = rangeList;
    if (colourList.length > 0) filterObject.web_colour = colourList;
    if (designList.length > 0) filterObject.design_style = designList;
    if (typesList.length > 0) filterObject.web_type = typesList;
    if (brandList.length > 0) filterObject.brand = brandList;
    if (materialList.length > 0) filterObject.web_materials = materialList;
    if (standardsList.length > 0) filterObject.web_standards = standardsList;
    if (sizeList.length > 0) filterObject.web_size = sizeList;
    if (shapeList.length > 0) filterObject.web_shape = shapeList;
    filterObject = mergeLocked(filterObject, lockedFilters);

    getProductsMutation.mutate({
      filters: filterObject,
      filter_fields: DEFAULT_FILTER_FIELDS,
      filter_match_types: buildMatchTypes(),
      main_filter_field: null,
      selection_fields: null,
      sort_by_setting: newSortValue,
      off_setter: { start: 0, end: isAll ? 50 : itemsPerPage },
      only_published: true,
      only_promo: false,
      only_in_stock: false,
      user_query: searchQueryRef.current || undefined,
      limited: true,
    });
  };

  const getInitialData = () => {
    const currentParams = new URLSearchParams(location.search);
    const rawApplicationParams = currentParams.getAll('application');

    const expandApplications = (apps: string[]): string[] => {
      const result = [...apps];
      if (apps.some(a => a.toLowerCase() === 'curtaining') && !result.includes('Dual Purpose')) {
        result.push('Dual Purpose');
      }
      if (apps.some(a => a.toLowerCase() === 'upholstery') && !result.includes('Dual Purpose')) {
        result.push('Dual Purpose');
      }
      return result;
    };

    const applicationParams = expandApplications(rawApplicationParams);

    const rangeParams = currentParams.getAll('range');
    const colourParams = currentParams.getAll('colours');
    const designParams = currentParams.getAll('design_style');
    const typeParams = currentParams.getAll('types');
    const brandParams = currentParams.getAll('brand');
    const materialParams = currentParams.getAll('material');
    const standardsParams = currentParams.getAll('standards');
    const compositionParams = currentParams.getAll('composition');
    const performanceParams = currentParams.getAll('performance');
    const sizeParams = currentParams.getAll('sizes');
    const shapeParams = currentParams.getAll('shapes');

    const searchParam = currentParams.get('search') || '';
    setSearchQuery(searchParam);
    searchQueryRef.current = searchParam;

    const sortParam = currentParams.get('sort') || 'Alphabetical A-Z';
    const rawItemsPerPage = currentParams.get('itemsPerPage');
    const itemsPerPageParam = rawItemsPerPage ? parseInt(rawItemsPerPage, 10) : -1;
    const isShowAll = itemsPerPageParam === -1;

    // Derived from the path rather than state: a state value is still empty on
    // the first run, which sent an unfiltered request ahead of the real one.
    const applicationsToUse = applicationParams.length > 0
      ? applicationParams
      : getDefaultApplications(location.pathname);

    setApplicationsList(applicationsToUse);
    setRangeList(rangeParams);
    setColourList(colourParams);
    setDesignList(designParams);
    setTypesList(typeParams);
    setBrandList(brandParams);
    setMaterialList(materialParams);
    setStandardsList(standardsParams);
    setCompositionList(compositionParams);
    setPerformanceList(performanceParams);
    setSizeList(sizeParams);
    setShapeList(shapeParams);
    setSortBySetting(sortParam);
    setItemsPerPage(itemsPerPageParam);
    setShowAll(isShowAll);

    const page = parseInt(currentParams.get('page') || '1');
    const startOffset = parseInt(currentParams.get('startoffset') || '0');
    const endOffset = isShowAll
      ? 50
      : parseInt(currentParams.get('endoffset') || itemsPerPageParam.toString());

    restoreRef.current = null;
    let fetchStart = startOffset;
    let fetchEnd = endOffset;
    if (navigationType === 'POP') {
      try {
        const raw = sessionStorage.getItem(SCROLL_RESTORE_KEY);
        if (raw) {
          const blob = JSON.parse(raw);
          const matches = blob
            && blob.path === `${window.location.pathname}${window.location.search}`
            && Date.now() - (blob.ts || 0) < SCROLL_RESTORE_MAX_AGE_MS;
          if (matches && blob.showAll) {
            const count = Math.max(blob.loadedCount || 0, 50);
            setShowAll(true);
            setItemsPerPage(-1);
            restoreRef.current = blob;
            fetchStart = 0;
            fetchEnd = count;
          } else if (!matches) {
            sessionStorage.removeItem(SCROLL_RESTORE_KEY);
          }
        }
      } catch { /* ignore */ }
    }

    let filterObject: MultiFilterMutationRequest['filters'] = { item_group: itemGroup };
    if (applicationsToUse.length > 0) filterObject.application = applicationsToUse;
    if (rangeParams.length > 0) filterObject.range = rangeParams;
    if (colourParams.length > 0) filterObject.web_colour = colourParams;
    if (designParams.length > 0) filterObject.design_style = designParams;
    if (typeParams.length > 0) filterObject.web_type = typeParams;
    if (brandParams.length > 0) filterObject.brand = brandParams;
    if (materialParams.length > 0) filterObject.web_materials = materialParams;
    if (standardsParams.length > 0) filterObject.web_standards = standardsParams;
    if (compositionParams.length > 0) filterObject.web_composition = compositionParams;
    if (performanceParams.length > 0) filterObject.web_performance = performanceParams;
    if (sizeParams.length > 0) filterObject.web_size = sizeParams;
    if (shapeParams.length > 0) filterObject.web_shape = shapeParams;
    filterObject = mergeLocked(filterObject, lockedFilters);

    getProductsMutation.mutate({
      filters: filterObject,
      filter_fields: DEFAULT_FILTER_FIELDS,
      filter_match_types: buildMatchTypes(),
      main_filter_field: null,
      selection_fields: null,
      sort_by_setting: sortParam,
      off_setter: { start: fetchStart, end: fetchEnd },
      only_published: true,
      only_promo: false,
      only_in_stock: false,
      user_query: searchQueryRef.current || undefined,
      limited: true,
    });

    setCurrentPage(page);
  };

  const saveCurrentPosition = () => {
    const currentPosition = window.scrollY || document.documentElement.scrollTop;
    sessionStorage.setItem('multifilter_scroll_position', currentPosition.toString());
    sessionStorage.setItem('multifilter_item_group', itemGroup);
    sessionStorage.setItem('multifilter_active', 'true');
    if (location.pathname.includes('/item_group/') && !location.pathname.includes('/application/')) {
      sessionStorage.setItem('changing_from_category_to_application', 'true');
    }
  };

  const clearScrollRestore = () => {
    try { sessionStorage.removeItem(SCROLL_RESTORE_KEY); } catch { /* ignore */ }
  };

  const saveScrollForRestore = () => {
    const loadedCount = showAll
      ? (accumulatedProducts.length || getProductsMutation.data?.products?.length || 0)
      : (getProductsMutation.data?.products?.length || 0);
    const gridEl = document.getElementById('multifilter-product-scroll');
    const blob = {
      path: `${window.location.pathname}${window.location.search}`,
      scrollY: window.scrollY || document.documentElement.scrollTop || 0,
      gridScrollTop: gridEl ? gridEl.scrollTop : 0,
      showAll,
      itemsPerPage,
      loadedCount,
      ts: Date.now(),
    };
    try { sessionStorage.setItem(SCROLL_RESTORE_KEY, JSON.stringify(blob)); } catch { /* ignore */ }
  };

  const scrollToFilterComponent = () => {
    requestAnimationFrame(() => {
      if (anchorRef.current) {
        const currentScrollPosition = window.pageYOffset || document.documentElement.scrollTop;
        const rect = anchorRef.current.getBoundingClientRect();
        window.scrollTo({ top: rect.top + currentScrollPosition - 80, behavior: 'smooth' });
      }
    });
  };

  const createPaginationRequest = (startOffset: number, endOffset: number): MultiFilterMutationRequest => {
    let filterObject: MultiFilterMutationRequest['filters'] = { item_group: itemGroup };
    if (applicationsList.length > 0) filterObject.application = applicationsList;
    if (rangeList.length > 0) filterObject.range = rangeList;
    if (colourList.length > 0) filterObject.web_colour = colourList;
    if (designList.length > 0) filterObject.design_style = designList;
    if (typesList.length > 0) filterObject.web_type = typesList;
    if (brandList.length > 0) filterObject.brand = brandList;
    if (materialList.length > 0) filterObject.web_materials = materialList;
    if (standardsList.length > 0) filterObject.web_standards = standardsList;
    if (sizeList.length > 0) filterObject.web_size = sizeList;
    if (shapeList.length > 0) filterObject.web_shape = shapeList;
    filterObject = mergeLocked(filterObject, lockedFilters);

    return {
      filters: filterObject,
      filter_fields: DEFAULT_FILTER_FIELDS,
      filter_match_types: buildMatchTypes(),
      main_filter_field: null,
      selection_fields: null,
      sort_by_setting: sortBySetting,
      off_setter: { start: startOffset, end: endOffset },
      only_published: true,
      only_promo: false,
      only_in_stock: false,
      user_query: searchQueryRef.current || undefined,
      limited: true,
    };
  };

  const nextPage = () => {
    if (currentPage === totalPages) return;
    saveCurrentPosition();
    const nextPageNum = currentPage + 1;
    const startOffset = (nextPageNum - 1) * itemsPerPage;
    const endOffset = startOffset + itemsPerPage;
    const p = new URLSearchParams(window.location.search);
    p.set('page', nextPageNum.toString());
    p.set('startoffset', startOffset.toString());
    p.set('endoffset', endOffset.toString());
    p.set('sort', sortBySetting);
    p.set('itemsPerPage', itemsPerPage.toString());
    window.history.replaceState({ path: `${location.pathname}?${p.toString()}` }, '', `${location.pathname}?${p.toString()}`);
    setCurrentPage(nextPageNum);
    scrollToFilterComponent();
    getProductsMutation.mutate(createPaginationRequest(startOffset, endOffset));
  };

  const previousPage = () => {
    if (currentPage === 1) return;
    saveCurrentPosition();
    const prevPageNum = currentPage - 1;
    const startOffset = (prevPageNum - 1) * itemsPerPage;
    const endOffset = startOffset + itemsPerPage;
    const p = new URLSearchParams(window.location.search);
    p.set('page', prevPageNum.toString());
    p.set('startoffset', startOffset.toString());
    p.set('endoffset', endOffset.toString());
    p.set('sort', sortBySetting);
    p.set('itemsPerPage', itemsPerPage.toString());
    window.history.replaceState({ path: `${location.pathname}?${p.toString()}` }, '', `${location.pathname}?${p.toString()}`);
    setCurrentPage(prevPageNum);
    scrollToFilterComponent();
    getProductsMutation.mutate(createPaginationRequest(startOffset, endOffset));
  };

  const lastPage = () => {
    if (currentPage === totalPages) return;
    saveCurrentPosition();
    const startOffset = (totalPages - 1) * itemsPerPage;
    const endOffset = startOffset + itemsPerPage;
    const p = new URLSearchParams(window.location.search);
    p.set('page', totalPages.toString());
    p.set('startoffset', startOffset.toString());
    p.set('endoffset', endOffset.toString());
    p.set('sort', sortBySetting);
    p.set('itemsPerPage', itemsPerPage.toString());
    window.history.replaceState({ path: `${location.pathname}?${p.toString()}` }, '', `${location.pathname}?${p.toString()}`);
    setCurrentPage(totalPages);
    scrollToFilterComponent();
    getProductsMutation.mutate(createPaginationRequest(startOffset, endOffset));
  };

  const firstPage = () => {
    if (currentPage === 1) return;
    saveCurrentPosition();
    const p = new URLSearchParams(window.location.search);
    p.set('page', '1');
    p.set('startoffset', '0');
    p.set('endoffset', itemsPerPage.toString());
    p.set('sort', sortBySetting);
    p.set('itemsPerPage', itemsPerPage.toString());
    window.history.replaceState({ path: `${location.pathname}?${p.toString()}` }, '', `${location.pathname}?${p.toString()}`);
    setCurrentPage(1);
    scrollToFilterComponent();
    getProductsMutation.mutate(createPaginationRequest(0, itemsPerPage));
  };

  useEffect(() => {
    getInitialData();
  }, [location.search]);

  useEffect(() => {
    if (navigationType !== 'POP') return;
    let blob: any;
    try {
      const raw = sessionStorage.getItem(SCROLL_RESTORE_KEY);
      if (!raw) return;
      blob = JSON.parse(raw);
    } catch { return; }
    if (!blob || blob.path !== `${window.location.pathname}${window.location.search}`) return;
    if (Date.now() - (blob.ts || 0) >= SCROLL_RESTORE_MAX_AGE_MS) return;

    const targetY: number = blob.scrollY || 0;
    const targetGrid: number = blob.gridScrollTop || 0;
    const deadline = Date.now() + SCROLL_RESTORE_TIMEOUT_MS;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let cancelled = false;

    const stop = () => {
      cancelled = true;
      clearScrollRestore();
      window.removeEventListener('wheel', onUserScroll);
      window.removeEventListener('touchmove', onUserScroll);
      window.removeEventListener('keydown', onUserScroll);
      if (timer) clearTimeout(timer);
    };

    const onUserScroll = () => stop();

    const tick = () => {
      if (cancelled) return;
      if (!sessionStorage.getItem(SCROLL_RESTORE_KEY)) { stop(); return; }

      const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
      window.scrollTo(0, Math.max(0, Math.min(targetY, maxScroll)));

      const gridEl = document.getElementById('multifilter-product-scroll');
      if (gridEl) {
        const gridMax = gridEl.scrollHeight - gridEl.clientHeight;
        gridEl.scrollTop = Math.max(0, Math.min(targetGrid, gridMax));
      }

      if (Date.now() < deadline) {
        timer = setTimeout(tick, 50);
      } else {
        stop();
      }
    };

    window.addEventListener('wheel', onUserScroll, { passive: true });
    window.addEventListener('touchmove', onUserScroll, { passive: true });
    window.addEventListener('keydown', onUserScroll);
    timer = setTimeout(tick, 0);

    return () => {
      window.removeEventListener('wheel', onUserScroll);
      window.removeEventListener('touchmove', onUserScroll);
      window.removeEventListener('keydown', onUserScroll);
      if (timer) clearTimeout(timer);
    };
  }, []);

  useEffect(() => {
    const defaults = getDefaultApplications(location.pathname);

    // Put the default applications in the URL. The navigation changes
    // location.search, which re-runs getInitialData and fetches the grid — no
    // request is made from here.
    if (!location.search && defaults.length > 0) {
      setApplicationsList(defaults);
      const params = new URLSearchParams();
      defaults.forEach((app: string) => params.append('application', app));
      params.set('itemsPerPage', '-1'); // Set default query param
      navigate(`${location.pathname}?${params.toString()}`, { replace: true });
    }
  }, [location.pathname, navigate, itemGroup]);

  useEffect(() => {
    return () => {
      if (debouncedApiCall.current) clearTimeout(debouncedApiCall.current);
      if (abortControllerRef.current) abortControllerRef.current.abort();
    };
  }, []);

  return (
    <Container maxWidth="xl">
      {!hideHeader && (
        <Box sx={{ mt: 6, mb: 2 }}>
          <Typography variant="h3" fontWeight={400} fontSize="40px" textTransform="capitalize" sx={{ mb: 1 }}>
            {`Discover – ${title}`}
          </Typography>
          <Typography fontWeight={400} fontSize="18px">
            Our inventory features thousands of designs. Use the selection bar below to find exactly what you're looking
            for.
          </Typography>
        </Box>
      )}

      {/* Header divider line above the multifilter (matches trade). A plain
          backgroundColor is invisible on a zero-height <hr>, so draw it as a
          1px top border. When the header is hidden, add top spacing like trade. */}
      {/* CMS block heading: H2 title + P2 description, same rhythm as the
          standard header (title → 8px → copy → 24px → rule). Either can be
          empty (e.g. no description) — only what's set is rendered, and a lone
          title keeps the full 24px before the rule. */}
      {hideHeader && sectionHeader && (sectionHeader.title || sectionHeader.description) && (
        <Box>
          {sectionHeader.title && (
            <Typography component="h2" sx={{ ...styleFor('H2'), mb: sectionHeader.description ? 1 : 3 }}>
              {sectionHeader.title}
            </Typography>
          )}
          {sectionHeader.description && (
            <Typography component="p" sx={{ ...styleFor('P2'), mb: 3, whiteSpace: 'pre-line' }}>
              {sectionHeader.description}
            </Typography>
          )}
        </Box>
      )}

      <Divider sx={{ border: 0, borderTop: '1px solid #000', mb: 3, mt: hideHeader && !flush ? 6 : 0 }} />

      <Box ref={anchorRef} id="filter-component-section">
        <Box
          sx={{
            // Match trade exactly: CSS grid 3fr / 9fr with a 24px column gap.
            display: { xs: 'block', lg: 'grid' },
            gridTemplateColumns: { lg: '3fr 9fr' },
            columnGap: { lg: 3 },
            alignItems: 'start',
            width: '100%',
            mb: flush ? 0 : '4rem',
          }}
        >
          {/* Sticky Sidebar Container — pins below the navbar and scrolls
              its own content (search + tags + filters) independently, while
              the product grid scrolls with the page (mirrors trade). */}
          <Box
            ref={filterScrollRef}
            sx={{
              position: { lg: 'sticky' },
              top: { lg: `${navOffset}px` },
              alignSelf: 'start',
              maxHeight: { lg: `calc(100vh - ${navOffset}px)` },
              overflowY: { lg: 'auto' },
              overscrollBehavior: 'contain',
              scrollbarGutter: 'stable',
              pr: { lg: 1 },
              mb: 4,
              // Auto-hide scrollbar: gutter is reserved (no layout shift) but the
              // thumb only shows while the `is-scrolling` class is set.
              scrollbarWidth: 'thin',
              scrollbarColor: 'transparent transparent',
              '&.is-scrolling': { scrollbarColor: 'rgba(0,0,0,0.35) transparent' },
              '&::-webkit-scrollbar': { width: '8px' },
              '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'transparent',
                borderRadius: '4px',
                transition: 'background-color 0.3s ease',
              },
              '&.is-scrolling::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(0,0,0,0.35)' },
            }}
          >
            {/* Filter Sidebar Content Wrapper */}
            <Box>
              {/* Search Products (matches trade: heading 1.2rem, real search
                  that filters on Enter via user_query) */}
              <ProductSearch
                onSearch={handleSearchChange}
                isLoading={getProductsMutation.isLoading}
                initialValue={searchQuery}
              />

              {/* Filter Tags / Active Badges with Clear all — the "Filters"
                  heading lives inside FilterTags so it only appears (with the
                  tags) once a filter is active, matching trade. */}
              <FilterTags
                rangeList={rangeList}
                colourList={colourList}
                designList={designList}
                typesList={typesList}
                brandList={brandList}
                materialList={materialList}
                standardsList={standardsList}
                compositionList={compositionList}
                performanceList={performanceList}
                sizeList={sizeList}
                shapeList={shapeList}
                onRemoveFilter={handleRemoveFilter}
                onClearAll={handleClearAllFilters}
              />

              {/* Filter Option Accordions */}
              <FilterSelection
                availableRanges={displayRanges}
                availableColours={availableColours}
                availableDesigns={availableDesigns}
                availableTypes={availableTypes}
                availableBrands={availableBrands}
                availableMaterials={availableMaterials}
                availableStandards={availableStandards}
                availableCompositions={availableCompositions}
                availablePerformances={availablePerformances}
                availableSizes={availableSizes}
                availableShapes={availableShapes}
                availableApplications={availableApplications}
                filteredRanges={filteredRanges}
                filteredColours={filteredColours}
                filteredDesigns={filteredDesigns}
                filteredTypes={filteredTypes}
                filteredBrands={filteredBrands}
                filteredMaterials={filteredMaterials}
                filteredStandards={filteredStandards}
                filteredCompositions={filteredCompositions}
                filteredPerformances={filteredPerformances}
                filteredSizes={filteredSizes}
                filteredShapes={filteredShapes}
                rangeList={rangeList}
                colourList={colourList}
                designList={designList}
                typesList={typesList}
                brandList={brandList}
                materialList={materialList}
                standardsList={standardsList}
                compositionList={compositionList}
                performanceList={performanceList}
                sizeList={sizeList}
                shapeList={shapeList}
                setRangeList={setRangeList}
                setColourList={setColourList}
                setDesignList={setDesignList}
                setTypesList={setTypesList}
                setBrandList={setBrandList}
                setMaterialList={setMaterialList}
                setStandardsList={setStandardsList}
                setCompositionList={setCompositionList}
                setPerformanceList={setPerformanceList}
                setSizeList={setSizeList}
                setShapeList={setShapeList}
                handleCheckboxChange={handleCheckboxChange}
                applicationsList={applicationsList}
                setApplicationsList={setApplicationsList}
                applyFiltersForApplications={applyFiltersForApplications}
                itemGroup={itemGroup}
                isLoading={getProductsMutation.isLoading}
                // Item-group Category filter — pinned above Applications on
                // cross-category pages (item_group_names present). web_type is
                // relabelled "Types" so it doesn't clash with the "Category" label.
                showCategory={Object.keys(availableCategories).length > 0}
                selectedCategories={categoryList}
                availableCategories={availableCategories}
                onCategoryChange={handleCategoryChange}
                webTypeTitle={Object.keys(availableCategories).length > 0 ? 'Types' : 'Category'}
              />
            </Box>
          </Box>

          {/* Product Grid Area */}
          <Box sx={{ minWidth: 0, width: '100%' }}>
            {(getProductsMutation?.data?.products?.length === 0 || productItemCount === 0) && !getProductsMutation.isLoading ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  flexDirection: 'column',
                  width: '100%',
                  height: '60vh',
                }}
              >
                <Typography fontSize="1.5rem" fontWeight="bold">No items found</Typography>
                <Typography fontSize="1.5rem" fontWeight="bold">Please try a different selection</Typography>
              </Box>
            ) : getProductsMutation.isLoading ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  flexDirection: 'column',
                  width: '100%',
                  height: '60vh',
                }}
              >
                <Typography fontSize="1.5rem" fontWeight="bold">Updating...</Typography>
              </Box>
            ) : (
              <ProductGrid
                productItemCount={productItemCount}
                getProductsMutation={getProductsMutation}
                totalPages={totalPages}
                currentPage={currentPage}
                previousPage={previousPage}
                nextPage={nextPage}
                lastPage={lastPage}
                firstPage={firstPage}
                anchorRef={anchorRef}
                showAll={showAll}
                accumulatedProducts={accumulatedProducts}
                onLoadMore={handleLoadMore}
                loadingMore={loadingMore}
                loadMoreFailed={loadMoreFailed}
                currentSort={sortBySetting}
                onSortChange={handleSortChange}
                currentItemsPerPage={itemsPerPage}
                onItemsPerPageChange={handleItemsPerPageChange}
                hoverAnimationEnabled={hoverAnimationEnabled}
                onProductNavigate={saveScrollForRestore}
              />
            )}
          </Box>
        </Box>
      </Box>
    </Container>
  );
}

export default FilterComponent;